import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { addDays, addMonths, format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ManualSubscriptionTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  currentNextBillingDate: string | null;
  onSuccess: () => void;
}

export function ManualSubscriptionTransactionModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  currentNextBillingDate,
  onSuccess,
}: ManualSubscriptionTransactionModalProps) {
  const [paymentType, setPaymentType] = useState<string>("regular");
  const [periodType, setPeriodType] = useState<"days" | "months" | "lifetime">("months");
  const [periodValue, setPeriodValue] = useState<string>("1");
  const [isLifetime, setIsLifetime] = useState(false);
  const [observations, setObservations] = useState("");

  const createTransactionMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Validar período baseado no tipo
      let period: number;
      let expirationDate: Date | null = null;
      
      if (isLifetime || periodType === "lifetime") {
        period = 0; // Lifetime não tem período
        expirationDate = null; // NULL = lifetime
      } else if (periodType === "days") {
        period = parseInt(periodValue, 10);
        if (isNaN(period) || (period !== 1 && period !== 7)) {
          throw new Error("Para dias, informe apenas 1 ou 7 dias");
        }
      } else {
        period = parseInt(periodValue, 10);
        if (isNaN(period) || period < 1 || period > 24) {
          throw new Error("Número de meses inválido (1-24)");
        }
      }

      // Calcular datas
      const startDate = currentNextBillingDate 
        ? new Date(currentNextBillingDate) 
        : new Date();
      startDate.setHours(0, 0, 0, 0); // Zerar hora para consistência
      
      // Calcular expiration_date baseado no tipo de período
      if (!isLifetime && periodType !== "lifetime") {
        if (periodType === "days") {
          expirationDate = addDays(startDate, period);
        } else {
          expirationDate = addMonths(startDate, period);
        }
        expirationDate.setHours(23, 59, 59, 999); // Final do dia
      }
      
      // Determinar subscription_type baseado no payment_type
      let subscriptionType = 'regular';
      if (paymentType === 'cortesia') {
        subscriptionType = 'cortesia';
      } else if (paymentType === 'testers') {
        subscriptionType = 'testers';
      }
      // Para promotion, mantém subscription_type como 'regular'

      // Criar UMA transação com duração definida
      const transaction = {
        tenant_id: tenantId,
        transaction_type: "payment",
        payment_method: paymentType === 'regular' ? "manual" : paymentType,
        payment_type: paymentType,
        subscription_plan_id: "plus",
        amount: 0, // Sem cobrança para esses tipos
        status: "approved",
        payment_date: startDate.toISOString(),
        expiration_date: expirationDate ? expirationDate.toISOString() : null, // NULL = lifetime
        created_by: user.id,
        metadata: {
          payment_type: paymentType,
          period_type: periodType,
          period_value: period,
          lifetime: isLifetime || periodType === "lifetime",
          observations,
          created_by_name: user.email,
        },
      };

      // Inserir a transação
      const { error: transactionError } = await supabase
        .from("subscription_transactions")
        .insert(transaction);

      if (transactionError) throw transactionError;

      // Atualizar tenant
      const updateData: any = {
        subscription_status: "active",
        subscription_plan_id: "plus",
        subscription_type: subscriptionType,
        subscription_payment_method: paymentType === 'regular' ? "manual" : paymentType,
        subscription_start_date: startDate.toISOString(),
        subscription_last_payment_date: startDate.toISOString(),
      };
      
      // Definir próxima cobrança baseado no expiration_date ou lifetime
      if (expirationDate) {
        updateData.subscription_next_billing_date = expirationDate.toISOString();
      } else {
        // Lifetime: não definir próxima cobrança
        updateData.subscription_next_billing_date = null;
      }
      
      const { error: tenantError } = await supabase
        .from("tenants")
        .update(updateData)
        .eq("id", tenantId);

      if (tenantError) throw tenantError;
    },
    onSuccess: () => {
      let message = "";
      if (isLifetime || periodType === "lifetime") {
        message = `Assinatura ${getPaymentTypeLabel(paymentType)} para sempre lançada com sucesso!`;
      } else if (periodType === "days") {
        const period = parseInt(periodValue, 10);
        message = `${period} ${period === 1 ? 'dia' : 'dias'} de assinatura ${getPaymentTypeLabel(paymentType)} lançado${period > 1 ? 's' : ''} com sucesso!`;
      } else {
        const period = parseInt(periodValue, 10);
        message = `${period} ${period === 1 ? 'mês' : 'meses'} de assinatura ${getPaymentTypeLabel(paymentType)} lançado${period > 1 ? 's' : ''} com sucesso!`;
      }
      
      toast.success(message);
      setPaymentType("regular");
      setPeriodType("months");
      setPeriodValue("1");
      setIsLifetime(false);
      setObservations("");
      onSuccess();
    },
    onError: (error: any) => {
      toast.error("Erro ao lançar transação: " + error.message);
    },
  });

  const getPaymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      regular: "regular (sem cobrança)",
      cortesia: "cortesia",
      testers: "testers",
      promotion: "promoção",
    };
    return labels[type] || type;
  };

  const handleCreate = () => {
    createTransactionMutation.mutate();
  };

  const startDate = currentNextBillingDate 
    ? new Date(currentNextBillingDate) 
    : new Date();
  startDate.setHours(0, 0, 0, 0);
  
  let finalDate: Date | null = null;
  if (!isLifetime && periodType !== "lifetime") {
    const period = parseInt(periodValue, 10) || 1;
    if (periodType === "days") {
      finalDate = addDays(startDate, period);
    } else {
      finalDate = addMonths(startDate, period);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lançar Transações de Assinatura</DialogTitle>
          <DialogDescription>
            Lançar meses de assinatura para <strong>{tenantName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="payment-type">Tipo de Pagamento</Label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger id="payment-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular (sem cobrança)</SelectItem>
                <SelectItem value="cortesia">Cortesia</SelectItem>
                <SelectItem value="testers">Beta Tester</SelectItem>
                <SelectItem value="promotion">Promoção</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Todos os tipos não cobram e dão acesso normalmente
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="period-type">Tipo de Período</Label>
            <Select value={periodType} onValueChange={(value) => {
              setPeriodType(value as "days" | "months" | "lifetime");
              setIsLifetime(value === "lifetime");
              if (value === "days") {
                setPeriodValue("1");
              } else if (value === "months") {
                setPeriodValue("1");
              }
            }}>
              <SelectTrigger id="period-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="days">Dias</SelectItem>
                <SelectItem value="months">Meses</SelectItem>
                <SelectItem value="lifetime">Para sempre (sem vencimento)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {periodType !== "lifetime" && !isLifetime && (
            <div className="space-y-2">
              <Label htmlFor="period-value">
                {periodType === "days" ? "Número de Dias (1 ou 7)" : "Número de Meses (1-24)"}
              </Label>
              {periodType === "days" ? (
                <Select value={periodValue} onValueChange={setPeriodValue}>
                  <SelectTrigger id="period-value">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 dia</SelectItem>
                    <SelectItem value="7">7 dias</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="period-value"
                  type="number"
                  min="1"
                  max="24"
                  value={periodValue}
                  onChange={(e) => setPeriodValue(e.target.value)}
                />
              )}
              {finalDate && (
                <p className="text-xs text-muted-foreground">
                  Período: {format(startDate, "dd/MM/yyyy", { locale: ptBR })} até {format(finalDate, "dd/MM/yyyy", { locale: ptBR })}
                </p>
              )}
            </div>
          )}

          {isLifetime || periodType === "lifetime" ? (
            <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                ⚠️ Esta assinatura será <strong>para sempre</strong> (sem vencimento). O tenant terá acesso permanente.
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="observations">Observações (opcional)</Label>
            <Textarea
              id="observations"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Adicione observações sobre esta transação..."
              rows={3}
            />
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Esta ação irá:</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              <li>Criar 1 transação na tabela subscription_transactions</li>
              <li>Ativar o tenant como "active"</li>
              <li>Tipo: {getPaymentTypeLabel(paymentType)}</li>
              {isLifetime || periodType === "lifetime" ? (
                <li>Duração: <strong>Para sempre</strong> (sem vencimento)</li>
              ) : finalDate ? (
                <li>Expira em: {format(finalDate, "dd/MM/yyyy", { locale: ptBR })}</li>
              ) : null}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createTransactionMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createTransactionMutation.isPending || (periodType !== "lifetime" && !isLifetime && (!periodValue || parseInt(periodValue, 10) < 1))}
          >
            {createTransactionMutation.isPending 
              ? "Lançando..." 
              : isLifetime || periodType === "lifetime"
              ? "Lançar Para Sempre"
              : periodType === "days"
              ? `Lançar ${periodValue} ${parseInt(periodValue, 10) === 1 ? 'Dia' : 'Dias'}`
              : `Lançar ${periodValue} ${parseInt(periodValue, 10) === 1 ? 'Mês' : 'Meses'}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

